import React, { useEffect, useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Loader2, FileText } from "lucide-react";

const CAMERA_MOVES = [
  { v: "push_in", l: "Slow push-in", p: "slow cinematic dolly push-in moving gently toward the space" },
  { v: "pull_out", l: "Slow pull-out", p: "slow cinematic dolly pull-out gradually revealing the whole space" },
  { v: "pan_left", l: "Pan left", p: "smooth steady camera pan to the left across the room" },
  { v: "pan_right", l: "Pan right", p: "smooth steady camera pan to the right across the room" },
  { v: "tilt_up", l: "Tilt up (reveal)", p: "slow camera tilt upward revealing the height and ceiling of the space" },
  { v: "tilt_down", l: "Tilt down", p: "slow graceful camera tilt downward across the space" },
  { v: "orbit", l: "Orbit / arc", p: "smooth arcing orbit camera movement circling around the space" },
  { v: "crane_up", l: "Crane up", p: "cinematic crane shot rising smoothly upward over the space" },
  { v: "dolly", l: "Dolly through", p: "smooth cinematic dolly move tracking gently and steadily through the scene" },
  { v: "hold_zoom", l: "Hold, then slow zoom", p: "hold steady on the scene for a beat, then a slow, gentle zoom-in" },
  { v: "static", l: "Static / locked-off", p: "locked-off static camera with no movement, only subtle natural light shifts" },
];

export default function ScriptPanel({ project }) {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/transcript`).then((r) => setDoc(r.data && r.data.scenes ? r.data : null)).catch(() => {});
  }, [project.id, project.updated_at]);

  if (!doc)
    return (
      <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-600" data-testid="script-empty">
        The script is written after photos are analysed.
      </div>
    );

  const updScene = (idx, k, v) =>
    setDoc({ ...doc, scenes: doc.scenes.map((s) => (s.index === idx ? { ...s, [k]: v } : s)) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/transcript`, {
        title: doc.title,
        intro_narration: doc.intro_narration,
        scenes: doc.scenes.map((s) => ({
          index: s.index, narration: s.narration, motion_prompt: s.motion_prompt,
          caption: s.caption, duration: Number(s.duration) || 5,
        })),
      });
      toast.success("Script saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" data-testid="script-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-amber-500">
          <FileText className="w-4 h-4" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Scene Plan</span>
        </div>
        <Button data-testid="save-script-button" onClick={save} disabled={saving} className="h-9 rounded-sm bg-white text-zinc-950 hover:bg-zinc-200 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </div>

      <Input
        data-testid="script-title"
        value={doc.title || ""}
        onChange={(e) => setDoc({ ...doc, title: e.target.value })}
        className="bg-transparent border-0 border-b border-zinc-800 rounded-none font-display text-2xl px-0 focus-visible:ring-0 focus-visible:border-amber-500 h-auto py-2"
      />
      <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <span className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500">Agent intro</span>
        <Textarea
          data-testid="script-intro"
          value={doc.intro_narration || ""}
          onChange={(e) => setDoc({ ...doc, intro_narration: e.target.value })}
          className="mt-2 bg-transparent border-transparent rounded-sm resize-none focus-visible:ring-1 focus-visible:ring-amber-500/50 focus-visible:border-amber-500"
        />
      </div>

      <div className="mt-6 space-y-3">
        <p className="text-[11px] text-zinc-600">Set each clip's length (4–15s). Shorter clips render faster and cost fewer credits — the video will match this exactly.</p>
        {doc.scenes.map((s) => (
          <div key={s.index} className="grid grid-cols-4 gap-4 bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={`scene-${s.index}`}>
            <div className="col-span-1">
              <img src={mediaUrl(s.media_id)} alt="" className="w-full h-24 object-cover rounded-sm border border-zinc-800" />
              <span className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-600 mt-2 block">Scene {s.index + 1}</span>
            </div>
            <div className="col-span-3 space-y-2">
              <Textarea
                value={s.narration || ""}
                onChange={(e) => updScene(s.index, "narration", e.target.value)}
                placeholder="Narration"
                className="bg-transparent border-transparent rounded-sm resize-none text-sm focus-visible:ring-1 focus-visible:ring-amber-500/50 focus-visible:border-amber-500"
              />
              <div>
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">Caption (lower third)</label>
                <Input
                  data-testid={`scene-caption-${s.index}`}
                  value={s.caption || ""}
                  onChange={(e) => updScene(s.index, "caption", e.target.value)}
                  placeholder="On-screen text for this clip"
                  className="bg-zinc-950 border-zinc-800 rounded-sm text-xs h-9"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">Camera motion — describe any shot you want</label>
                  <select
                    data-testid={`scene-camera-preset-${s.index}`}
                    value=""
                    onChange={(e) => {
                      const m = CAMERA_MOVES.find((x) => x.v === e.target.value);
                      if (m) updScene(s.index, "motion_prompt", m.p);
                    }}
                    className="w-full h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-400 px-2 mb-2 focus:border-amber-500 outline-none"
                  >
                    <option value="">Insert a preset (optional)…</option>
                    {CAMERA_MOVES.map((m) => (
                      <option key={m.v} value={m.v}>{m.l}</option>
                    ))}
                  </select>
                  <Textarea
                    data-testid={`scene-camera-${s.index}`}
                    value={s.motion_prompt || ""}
                    onChange={(e) => updScene(s.index, "motion_prompt", e.target.value)}
                    placeholder="Write it your way — e.g. 'drone rises over the barn then pushes in on the colt', 'handheld walk toward the front door'…"
                    className="bg-zinc-950 border-zinc-800 rounded-sm text-xs min-h-[3.75rem] resize-y focus-visible:ring-1 focus-visible:ring-amber-500/50 focus-visible:border-amber-500"
                  />
                </div>
                <div>
                  <label className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">Clip length (sec)</label>
                  <select
                    data-testid={`scene-duration-${s.index}`}
                    value={s.duration || 5}
                    onChange={(e) => updScene(s.index, "duration", e.target.value)}
                    className="w-full h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none"
                  >
                    {[4, 5, 6, 7, 8, 9, 10, 12, 15].map((n) => (
                      <option key={n} value={n}>{n}s</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
